import { Fragment, useState } from 'react';
import { Card, Form, Button, Row, Col, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { DefensoriaOpcao, BuscarRelatoriosDoDefensor, RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';
import { RELATORIO_CORES } from '../constants/relatorio-cores';
import { defensorPodeAbrirEditorDoRelatorioAtividade } from '../utils/relatorio-defensor-edicao.utils';

export interface CreateNewOptions {
  defensoriaId: number;
  defensoriaName: string;
  mes: string;
  ano: number;
  tipo: 'civil' | 'criminal';
}

interface RelatoriosAtividadesFilterProps {
  defensoriasOpcoes?: DefensoriaOpcao[];
  defensorId?: number;
  onFilterChange?: (filters: FilterOptions) => void;
  onCreateNew?: (options: CreateNewOptions) => void;
  onEditExisting?: (relatorioId: number, options: CreateNewOptions) => void;
  /** Abre relatório existente em modo somente leitura (ex.: já enviado). */
  onViewExisting?: (relatorio: RelatorioAtividadeDto) => void;
  /** Callback opcional para notificar mudança de período (mês/ano) ao componente pai. */
  onPeriodoChange?: (mes: string, ano: number) => void;
}

interface FilterOptions {
  defensoriaId?: number;
  mes?: string;
  ano?: number;
  tipo?: string;
  status?: string;
}

export default function RelatoriosAtividadesFilter({
  defensoriasOpcoes = [],
  defensorId,
  onFilterChange,
  onCreateNew,
  onEditExisting,
  onViewExisting,
  onPeriodoChange,
}: RelatoriosAtividadesFilterProps) {
  const currentDate = new Date();
  const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
  const currentYear = currentDate.getFullYear();

  const [defensoriaId, setDefensoriaId] = useState<string>('');
  const [mes, setMes] = useState<string>(currentMonth);
  const [ano, setAno] = useState<number>(currentYear);
  const [tipo, setTipo] = useState<'civil' | 'criminal'>('civil');
  const [showExistenteModal, setShowExistenteModal] = useState(false);
  const [relatorioExistente, setRelatorioExistente] = useState<RelatorioAtividadeDto | null>(null);
  const [opcoesCriacao, setOpcoesCriacao] = useState<CreateNewOptions | null>(null);
  const [existenteBloqueadoEdicao, setExistenteBloqueadoEdicao] = useState(false);
  const [verificando, setVerificando] = useState(false);

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!defensoriaId) {
      alert('Por favor, selecione uma defensoria/vara');
      return;
    }

    if (onFilterChange) {
      onFilterChange({
        defensoriaId: parseInt(defensoriaId),
        mes,
        ano,
        tipo,
      });
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const todosMeses = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
  ];

  // Não permitir criar relatório para meses futuros: no ano atual, só até o mês atual
  const meses =
    ano === currentYear
      ? todosMeses.filter((m) => parseInt(m.value, 10) <= parseInt(currentMonth, 10))
      : todosMeses;

  const cardStyle = {
    border: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px',
    boxShadow: RELATORIO_CORES.SOMBRA_CARD,
  };
  const headerStyle = {
    backgroundColor: RELATORIO_CORES.CINZA_CLARO,
    color: RELATORIO_CORES.PRIMARIA,
    borderBottom: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px 8px 0 0',
  };

  return (
    <Fragment>
      <Card className="mb-4" style={cardStyle}>
        <Card.Header style={headerStyle}>
          <h5 className="mb-0 fw-bold" style={{ color: RELATORIO_CORES.PRIMARIA }}>Criar Novo Relatório</h5>
        </Card.Header>
        <Card.Body style={{ backgroundColor: RELATORIO_CORES.BRANCO }}>
          <Form onSubmit={handleFilterSubmit}>
            <Row>
              <Col md={2} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold text-muted">Ano</Form.Label>
                  <Form.Select
                    value={ano}
                    onChange={(e) => {
                      const novoAno = Number(e.target.value);
                      setAno(novoAno);
                      // Se mudou para o ano atual e o mês selecionado é futuro, corrigir para o mês atual
                      if (novoAno === currentYear && parseInt(mes, 10) > parseInt(currentMonth, 10)) {
                        setMes(currentMonth);
                        if (onPeriodoChange) {
                          onPeriodoChange(currentMonth, novoAno);
                        }
                      } else if (onPeriodoChange) {
                        onPeriodoChange(mes, novoAno);
                      }
                    }}
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold text-muted">Mês</Form.Label>
                  <Form.Select
                    value={mes}
                    onChange={(e) => {
                      const novoMes = e.target.value;
                      setMes(novoMes);
                      if (onPeriodoChange) {
                        onPeriodoChange(novoMes, ano);
                      }
                    }}
                  >
                    <option value="">Selecione um mês</option>
                    {meses.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold text-muted">Defensoria</Form.Label>
                  <Form.Select
                    value={defensoriaId}
                    onChange={(e) => {
                      const newDefensoriaId = e.target.value;
                      setDefensoriaId(newDefensoriaId);
                      const selected = defensoriasOpcoes.find((d) => d.id === parseInt(newDefensoriaId, 10));
                      if (selected) {
                        if (selected.criminal && !selected.civel) setTipo('criminal');
                        else if (selected.civel && !selected.criminal) setTipo('civil');
                        else if (selected.criminal && selected.civel) setTipo('civil');
                      }
                    }}
                    required
                    disabled={defensoriasOpcoes.length === 0}
                  >
                    <option value="">Selecione uma defensoria</option>
                    {defensoriasOpcoes.map((def) => (
                      <option key={def.id} value={def.id}>
                        {def.nome} - {def.comarca}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold text-muted">Tipo</Form.Label>
                  <Form.Select
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value as 'civil' | 'criminal')}
                    required
                  >
                    <option value="civil">Cível</option>
                    <option value="criminal">Criminal</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2} className="mb-3 d-flex align-items-end">
                <Form.Group className="w-100 mb-0">
                  <Form.Label className="fw-bold" style={{ visibility: 'hidden' }}>
                    Ação
                  </Form.Label>
                  <Button
                    className="w-100"
                    style={{
                      backgroundColor: RELATORIO_CORES.PRIMARIA,
                      color: RELATORIO_CORES.TERCIARIA,
                      border: `1px solid ${RELATORIO_CORES.PRIMARIA}`,
                      borderRadius: '8px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      paddingLeft: '0.5rem',
                      paddingRight: '0.5rem',
                    }}
                    disabled={verificando}
                    onClick={async () => {
                      if (!defensoriaId) {
                        toast.warning('Por favor, selecione uma defensoria/vara para criar o relatório.');
                        return;
                      }

                      if (!mes) {
                        toast.warning('Por favor, selecione um mês.');
                        return;
                      }

                      // Não permitir criar relatório para período futuro
                      const mesNum = parseInt(mes, 10);
                      if (ano > currentYear || (ano === currentYear && mesNum > parseInt(currentMonth, 10))) {
                        toast.warning('Não é possível criar relatório para mês/ano futuro.');
                        return;
                      }

                      if (!defensorId) {
                        toast.error('ID do defensor não encontrado.');
                        return;
                      }

                      try {
                        setVerificando(true);
                        const defensoriaSelected = defensoriasOpcoes.find(d => d.id === parseInt(defensoriaId));
                        const opcoes: CreateNewOptions = {
                          defensoriaId: parseInt(defensoriaId),
                          defensoriaName: defensoriaSelected?.nome || '',
                          mes,
                          ano,
                          tipo,
                        };

                        // Verificar se já existe um relatório para o mesmo período, defensoria e tipo
                        const relatoriosExistentes = await BuscarRelatoriosDoDefensor(
                          defensorId,
                          opcoes.defensoriaId,
                          opcoes.mes,
                          opcoes.ano,
                          opcoes.tipo
                        );

                        // Normalizar valores para comparação
                        const mesNumero = parseInt(opcoes.mes);

                        // Verificar se existe relatório com exatamente os mesmos parâmetros
                        // O backend retorna mes como string no DTO
                        const relatorioExistente = relatoriosExistentes.find((r) => {
                          // Normalizar mês do relatório para comparação
                          let mesRelatorio: number;
                          if (typeof r.mes === 'string') {
                            // Remove zeros à esquerda para comparação numérica
                            mesRelatorio = parseInt(r.mes);
                          } else {
                            mesRelatorio = r.mes;
                          }

                          // Comparar todos os campos
                          const mesmaDefensoria = r.defensoriaId === opcoes.defensoriaId;
                          const mesmoMes = mesRelatorio === mesNumero;
                          const mesmoAno = r.ano === opcoes.ano;
                          const mesmoTipo = r.tipo === opcoes.tipo;

                          return mesmaDefensoria && mesmoMes && mesmoAno && mesmoTipo;
                        });

                        if (relatorioExistente) {
                          const podeEditar = defensorPodeAbrirEditorDoRelatorioAtividade(relatorioExistente.status);
                          setRelatorioExistente(relatorioExistente);
                          setOpcoesCriacao(opcoes);
                          setExistenteBloqueadoEdicao(!podeEditar);
                          setShowExistenteModal(true);
                          if (podeEditar) {
                            toast.info('Já existe um relatório para o período, defensoria e tipo selecionado. Deseja editá-lo?');
                          } else {
                            toast.warning(
                              'Já existe relatório para este período e ele não pode mais ser editado (enviado ou em tramitação).',
                            );
                          }
                        } else {
                          // Não existe relatório - criar novo
                          onCreateNew?.(opcoes);
                        }
                      } catch (error) {
                        console.error('Erro ao verificar relatório existente:', error);
                        toast.error('Erro ao verificar se o relatório já existe.');
                      } finally {
                        setVerificando(false);
                      }
                    }}
                  >
                    {verificando ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Verificando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-plus-lg me-1"></i>
                        Novo
                      </>
                    )}
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Modal de confirmação para editar relatório existente */}
      <Modal show={showExistenteModal} onHide={() => {
        setShowExistenteModal(false);
        setRelatorioExistente(null);
        setOpcoesCriacao(null);
        setExistenteBloqueadoEdicao(false);
      }} centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className={`bi me-2 ${existenteBloqueadoEdicao ? 'bi-lock-fill text-secondary' : 'bi-exclamation-triangle-fill text-warning'}`}></i>
            Relatório já existe
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={`alert ${existenteBloqueadoEdicao ? 'alert-secondary' : 'alert-warning'}`} role="alert">
            <strong>Atenção!</strong>{' '}
            {existenteBloqueadoEdicao ? (
              <>
                Já existe um relatório com o mesmo período, defensoria e tipo. Relatórios já enviados ou em tramitação na
                diretoria ou Corregedoria <strong>não podem ser editados</strong> por este fluxo.
              </>
            ) : (
              <>Já existe um relatório com o mesmo período, defensoria e tipo selecionado.</>
            )}
          </div>
          {relatorioExistente && (
            <div className="mb-3 p-3 bg-light rounded">
              <h6 className="mb-3">Detalhes do relatório existente:</h6>
              <p className="mb-2">
                <strong>Período:</strong> {relatorioExistente.periodo}
              </p>
              <p className="mb-2">
                <strong>Defensoria/Vara:</strong> {relatorioExistente.defensoriaName}
              </p>
              <p className="mb-2">
                <strong>Tipo:</strong> {relatorioExistente.tipo === 'civil' ? 'Cível' : 'Criminal'}
              </p>
              <p className="mb-0">
                <strong>Status:</strong> {
                  relatorioExistente.status === 'rascunho' ? (
                    <span className="badge bg-warning text-dark">Rascunho</span>
                  ) : relatorioExistente.status === 'pendente' ? (
                    <span className="badge bg-info">Pendente</span>
                  ) : relatorioExistente.status === 'enviado' ? (
                    <span className="badge bg-success">Enviado</span>
                  ) : (
                    relatorioExistente.status
                  )
                }
              </p>
            </div>
          )}
          {!existenteBloqueadoEdicao && (
            <p className="mb-0 fw-bold">
              Deseja editar este relatório existente?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowExistenteModal(false);
            setRelatorioExistente(null);
            setOpcoesCriacao(null);
            setExistenteBloqueadoEdicao(false);
          }}>
            <i className="bi bi-x-circle me-2"></i>
            {existenteBloqueadoEdicao ? 'Fechar' : 'Não, cancelar'}
          </Button>
          {existenteBloqueadoEdicao && relatorioExistente && onViewExisting && (
            <Button
              variant="primary"
              onClick={() => {
                onViewExisting(relatorioExistente);
                setShowExistenteModal(false);
                setRelatorioExistente(null);
                setOpcoesCriacao(null);
                setExistenteBloqueadoEdicao(false);
              }}
            >
              <i className="bi bi-eye me-2"></i>
              Somente visualizar
            </Button>
          )}
          {!existenteBloqueadoEdicao && (
            <Button variant="primary" onClick={() => {
              if (relatorioExistente && opcoesCriacao) {
                if (onEditExisting) {
                  onEditExisting(relatorioExistente.id, opcoesCriacao);
                } else {
                  // Fallback: chamar onCreateNew com o ID
                  onCreateNew?.(opcoesCriacao);
                }
                setShowExistenteModal(false);
                setRelatorioExistente(null);
                setOpcoesCriacao(null);
                setExistenteBloqueadoEdicao(false);
              }
            }}>
              <i className="bi bi-pencil-square me-2"></i>
              Sim, editar relatório
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
}
